<template>
  <div class="time-reporting-container">
    <!-- Floating Export Button -->
    <button @click="exportToPDF" class="export-btn floating" :disabled="loading">
      📄 {{ $t('pdf.exportPDF') }}
    </button>

    <div class="header">
      <h2 class="page-title">{{ $t('navigation.timeReporting') }}</h2>
    </div>

    <!-- Month Navigation -->
    <div class="month-navigation">
      <button @click="previousMonth" class="nav-btn">‹</button>
      <h3 class="current-month">{{ formatMonthYear(currentDate) }}</h3>
      <button @click="nextMonth" class="nav-btn">›</button>
    </div>

    <!-- Monthly Calendar Table -->
    <div class="calendar-container">
      <table class="calendar-table">
        <thead>
          <tr>
            <th>{{ $t('weekdays.mon') }}</th>
            <th>{{ $t('weekdays.tue') }}</th>
            <th>{{ $t('weekdays.wed') }}</th>
            <th>{{ $t('weekdays.thu') }}</th>
            <th>{{ $t('weekdays.fri') }}</th>
            <th>{{ $t('weekdays.sat') }}</th>
            <th>{{ $t('weekdays.sun') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="week in calendarWeeks" :key="week.id">
            <td 
              v-for="day in week" 
              :key="day.date" 
              :class="[getDayClasses(day), { 'has-pending-shift': hasPendingShift(day) }]"
              @click="selectDate(day)"
            >
                              <div class="day-content">
                <!-- First Row: Day Number and Status Dot -->
                <div class="day-header">
                  <span class="day-number">{{ day.day }}</span>
                  <span v-if="day.timeEntries && day.timeEntries.length" class="status-dot" :class="getDayStatus(day.timeEntries)">●</span>
                </div>
                
                <!-- Second Row: Shift Times -->
                <div v-if="day.assignedShifts && day.assignedShifts.length" class="shift-times-row">
                  <span class="shift-times">
                    <ul class="shift-list">
                      <li v-for="(shift, index) in day.assignedShifts" :key="shift.id" class="shift-list-item">
                        <span @click="shift.status === 'pending' ? openPendingShiftModal(shift) : null" 
                              :class="{ 'clickable-pending': shift.status === 'pending' }">
                          {{ formatTime(shift.start_time).substring(0,5) }} - {{ formatTime(shift.end_time).substring(0,5) }}
                        </span>
                        <span v-if="shift.status === 'pending'" class="pending-shift desktop-only">
                          <button @click.stop="approveShift(shift.id)" class="approve-btn">✔</button>
                          <button @click.stop="rejectShift(shift.id)" class="reject-btn">✘</button>
                        </span>
                      </li>
                    </ul>
                  </span>
                </div>
                
                <!-- Third Row: Reported Time - Hours -->
                <div v-if="day.timeEntries && day.timeEntries.length" class="reported-time">
                  <span class="reported-label">R:</span>
                  <span class="reported-hours">{{ getDayTotalHours(day.timeEntries) }}h</span>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Time Entry Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">{{ $t('timeReporting.timeEntriesFor') }} {{ formatSelectedDate }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        
        <!-- Existing Shifts List -->
        <div v-if="selectedDateEntries.length > 0" class="existing-shifts">
          <h4>{{ $t('timeReporting.existingShifts') }}</h4>
          <div v-for="(entry, index) in selectedDateEntries" :key="entry.id" class="shift-item">
            <div class="shift-header">
              <span class="shift-label">{{ $t('timeReporting.shift') }} {{ index + 1 }}</span>
              <button @click="editShift(index)" class="edit-btn">{{ $t('common.edit') }}</button>
              <button @click="deleteShift(entry.id, index)" class="delete-shift-btn">{{ $t('common.delete') }}</button>
            </div>
            <div class="shift-details">
              <span>{{ entry.start_time }} - {{ entry.end_time }}</span>
              <span class="shift-hours">({{ entry.totalHours }}h)</span>
            </div>
          </div>
        </div>

        <!-- Sick Leave Button -->
        <div class="sick-leave-row" style="margin: 1rem 0; text-align: center;">
          <button
            class="sick-leave-btn"
            :disabled="!hasAssignedShiftForSelectedDate || hasSickLeaveForSelectedDate || isSelectedDateInPast || loading"
            @click="reportSickLeave"
          >
            🤒 {{ hasSickLeaveForSelectedDate ? $t('timeReporting.sickLeaveAlreadyReported') : $t('timeReporting.reportSickLeave') }}
          </button>
        </div>
        <!-- Add/Edit Shift Form -->
        <div class="shift-form">
          <h4>{{ editingShiftIndex !== null ? $t('timeReporting.editShift') : $t('timeReporting.addNewShift') }}</h4>
          <form @submit.prevent="saveShift" class="modal-form">
          <div class="form-row">
            <div class="form-group">
              <label for="start_time">{{ $t('timeReporting.startTime') }}:</label>
              <input 
                v-model="selectedEntry.start_time" 
                id="start_time" 
                type="time"
                required 
                class="time-input"
              />
            </div>
            <div class="form-group">
              <label for="end_time">{{ $t('timeReporting.endTime') }}:</label>
              <input 
                v-model="selectedEntry.end_time" 
                id="end_time" 
                type="time" 
                required 
                class="time-input"
              />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="break_duration">
                {{ $t('timeReporting.breakDuration') }}:
                <span v-if="requiresLegalBreakComputed" class="required-indicator">*{{ $t('timeReporting.requiredBreak') }}</span>
              </label>
              <input 
                v-model="selectedEntry.break_duration" 
                id="break_duration" 
                type="number" 
                min="0" 
                max="480"
                :class="{ 'break-warning': requiresLegalBreakComputed && selectedEntry.break_duration < 30 }"
              />
              <div v-if="requiresLegalBreakComputed && selectedEntry.break_duration < 30" class="warning-message">
                ⚠️ {{ $t('timeReporting.legalBreakWarning') }}
              </div>
              <div v-if="hasBreakDiscrepancy" class="discrepancy-message">
                ⚠️ {{ $t('timeReporting.breakDiscrepancyWarning') }}
              </div>
            </div>
          </div>
          
          <!-- Time Calculations -->
          <div class="time-calculations">
            <h4>{{ $t('timeReporting.timeBreakdown') }}</h4>
            <div class="calc-grid">
              <div class="calc-item">
                <label>{{ $t('timeReporting.regularHours') }}:</label>
                <span class="calc-value">{{ calculatedRegularHours }}h</span>
              </div>
              <div class="calc-item">
                <label>{{ $t('pdf.ob1') }} (18:15-20:00):</label>
                <span class="calc-value">{{ calculatedOB1 }}h</span>
              </div>
              <div class="calc-item">
                <label>{{ $t('pdf.ob2') }} (20:00-05:00):</label>
                <span class="calc-value">{{ calculatedOB2 }}h</span>
              </div>
              <div class="calc-item">
                <label>{{ $t('pdf.ob3') }} ({{ $t('weekdays.sat') }} 12:00-{{ $t('weekdays.mon') }} 05:00):</label>
                <span class="calc-value">{{ calculatedOB3 }}h</span>
              </div>
              <div class="calc-item total">
                <label>{{ $t('timeReporting.totalHours') }}:</label>
                <span class="calc-value total">{{ calculatedHours }}h</span>
              </div>
            </div>
            <div v-if="workerShiftMismatch" class="warning-message">
              ⚠️ {{ $t('timeReporting.shiftMismatchWarning') }}
            </div>
          </div>
          <div class="form-group">
            <label for="description">{{ $t('timeReporting.comments') }}:</label>
            <textarea v-model="selectedEntry.description" id="description" rows="3" :placeholder="$t('timeReporting.describeWork')"></textarea>
          </div>
          <div class="modal-actions">
            <button type="button" @click="cancelShiftEdit" class="cancel-btn">{{ $t('common.cancel') }}</button>
            <button type="submit" :disabled="loading" class="save-btn">
              {{ loading ? $t('common.saving') : (editingShiftIndex !== null ? $t('timeReporting.updateShift') : $t('timeReporting.addShift') ) }}
            </button>
          </div>
          </form>
        </div>

        <!-- Day Summary -->
        <div v-if="selectedDateEntries.length > 0" class="day-summary">
          <h4>{{ $t('timeReporting.daySummary') }}</h4>
          <div class="summary-grid">
            <div class="summary-item">
              <label>{{ $t('timeReporting.totalHours') }}:</label>
              <span>{{ getDayTotalHours(selectedDateEntries) }}h</span>
            </div>
            <div class="summary-item">
              <label>{{ $t('timeReporting.totalShifts') }}:</label>
              <span>{{ selectedDateEntries.length }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showConfirmModal" class="modal-overlay" @click="cancelConfirm">
      <div class="modal confirm-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">{{ $t('common.confirm') }}</h3>
        </div>
        <div class="modal-body">
          <p>{{ confirmMessage }}</p>
        </div>
        <div class="modal-footer">
          <button @click="cancelConfirm" class="cancel-btn">{{ $t('common.cancel') }}</button>
          <button @click="confirmAction" class="delete-btn">{{ $t('common.confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- Pending Shift Approval Modal (Mobile) -->
    <div v-if="showPendingShiftModal" class="modal-overlay" @click="closePendingShiftModal">
      <div class="modal-container pending-shift-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ $t('timeReporting.approveShift') }}</h3>
          <button @click="closePendingShiftModal" class="close-btn">×</button>
        </div>
        <div class="modal-body" v-if="selectedPendingShift">
          <div class="shift-detail">
            <p class="shift-date">{{ formatFullDate(selectedPendingShift.date) }}</p>
            <p class="shift-time">
              {{ formatTime(selectedPendingShift.start_time) }} - {{ formatTime(selectedPendingShift.end_time) }}
            </p>
            <p class="shift-duration">{{ $t('shiftCalendar.duration') }}: {{ selectedPendingShift.duration }}</p>
          </div>
          <div class="approval-actions">
            <button @click="approveShiftAndClose(selectedPendingShift.id)" class="approve-btn-large">
              ✔ {{ $t('status.approve') }}
            </button>
            <button @click="rejectShiftAndClose(selectedPendingShift.id)" class="reject-btn-large">
              ✘ {{ $t('status.reject') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary -->
    <div class="monthly-summary">
      <h3 class="subtitle">{{ $t('timeReporting.monthlySummary') }}</h3>
      <div class="summary-stats">
        <div class="stat">
          <span class="stat-label">{{ $t('timeReporting.shiftHours') }}:</span>
          <span class="stat-value">{{ monthlyShiftHoursComputed }}h</span>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('timeReporting.reportedHours') }}:</span>
          <span class="stat-value">{{ monthlyReportedHoursComputed }}h</span>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('timeReporting.workedDays') }}:</span>
          <span class="stat-value">{{ workingDaysCountComputed }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('timeReporting.averagePerDay') }}:</span>
          <span class="stat-value">{{ averageHoursPerDayComputed }}h</span>
        </div>
      </div>
    </div>

   
    <!-- Error/Success Messages -->
    <div v-if="message" :class="['message', messageType]">
      {{ message }}
    </div>
  </div>
</template>

<script setup>

import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth.js'
import { timeEntryApi } from '../services/timeEntryApi.js'
import { obHoursApi } from '../services/obHoursApi.js'
import { shiftApi } from '../services/shiftApi.js'
import { useShiftStore } from '../stores/shift.js'
import { sickLeaveApi } from '../services/sickLeaveApi.js'
import {
  useTimeCalculations,
  calculateOB3Hours,
  calculateAllHours,
  requiresLegalBreak,
  calculateRequiredBreak,
  workingDaysCount,
  averageHoursPerDay,
  monthlyHours,
  getDayTotalHours,
  getDayStatus
} from '../composables/useTimeCalculations.js'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

// Use composable for time calculations
const {
  parseTimeToMinutes,
  roundToQuarterHour,
  formatQuarterHours,
  calculateTotalHours,
  isOB3Period,
  formatTime,
  calculateShiftDuration
} = useTimeCalculations()

const router = useRouter()
const authStore = useAuthStore()
const { t, locale } = useI18n()
const loading = ref(false)
const message = ref('')
const messageType = ref('success')
const showModal = ref(false)
const currentDate = ref(new Date())
const selectedDate = ref(null)
const timeEntries = ref({}) // Object with date as key
const assignedShifts = ref({}) // Object with date as key for assigned shifts
const sickLeaveDays = ref({}) // Object with date as key for sick leave days
const user = ref(null)
const shiftStore = useShiftStore()

const selectedEntry = ref({
  start_time: '',
  end_time: '',
  break_duration: 30,
  description: ''
})

const editingShiftIndex = ref(null)
const selectedDateEntries = ref([])

// Confirmation modal
const showConfirmModal = ref(false)
const confirmMessage = ref('')
const confirmCallback = ref(null)

// Pending shift approval modal
const showPendingShiftModal = ref(false)
const selectedPendingShift = ref(null)

// Computed properties
const calendarWeeks = computed(() => {
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()

  // Get first day of month and adjust for Monday start
  const firstDay = new Date(year, month, 1)
  const firstDayOfWeek = (firstDay.getDay() + 6) % 7 // Convert Sunday=0 to Monday=0

  // Get last day of month
  const lastDay = new Date(year, month + 1, 0).getDate()

  const weeks = []
  let currentWeek = []

  // Add empty cells for days before month starts
  for (let i = 0; i < firstDayOfWeek; i++) {
    currentWeek.push({ 
      date: null, 
      day: '', 
      isCurrentMonth: false, 
      isToday: false,
      timeEntries: [],
      assignedShifts: []
    })
  }

  // Add days of the month
  for (let day = 1; day <= lastDay; day++) {
    // Create date at noon to avoid timezone issues
    const date = new Date(year, month, day, 12, 0, 0)
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const isToday = dateString === new Date().toISOString().split('T')[0]

    // Debug log to check dateString and assignedShifts
    
    currentWeek.push({
      date: dateString,
      day: day,
      isCurrentMonth: true,
      isToday: isToday,
      timeEntries: timeEntries.value[dateString] || [],
      assignedShifts: assignedShifts.value[dateString] || [],
      isSickLeave: sickLeaveDays.value[dateString] || false
    })

    // Start new week after Sunday (when we have 7 days)
    if (currentWeek.length === 7) {
      weeks.push([...currentWeek])
      currentWeek = []
    }
  }

  // Fill remaining days of last week
  while (currentWeek.length < 7) {
    currentWeek.push({ 
      date: null, 
      day: '', 
      isCurrentMonth: false, 
      isToday: false,
      timeEntries: [],
      assignedShifts: []
    })
  }

  if (currentWeek.some(day => day.isCurrentMonth)) {
    weeks.push(currentWeek)
  }

  return weeks
})

const formatSelectedDate = computed(() => {
  if (!selectedDate.value) return ''
  return new Date(selectedDate.value).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
})

const calculatedHours = computed(() => {
  if (!selectedEntry.value.start_time || !selectedEntry.value.end_time) return '0.00'
  const [startH, startM] = selectedEntry.value.start_time.split(':').map(Number)
  const [endH, endM] = selectedEntry.value.end_time.split(':').map(Number)
  let start = startH * 60 + startM
  let end = endH * 60 + endM
  let durationMinutes = end - start
  if (durationMinutes < 0) durationMinutes += 24 * 60
  durationMinutes -= parseInt(selectedEntry.value.break_duration) || 0
  const hours = durationMinutes / 60
  return formatQuarterHours(Math.max(0, hours))
})

// OB Calculations
const calculatedRegularHours = computed(() => {
  if (!selectedEntry.value.start_time || !selectedEntry.value.end_time) return '0.00'
  
  const totalHours = parseFloat(calculatedHours.value)
  const ob1 = parseFloat(calculatedOB1.value)
  const ob2 = parseFloat(calculatedOB2.value) 
  const ob3 = parseFloat(calculatedOB3.value)
  
  const regular = totalHours - ob1 - ob2 - ob3
  return formatQuarterHours(Math.max(0, regular))
})

const calculatedOB1 = computed(() => {
  if (!selectedEntry.value.start_time || !selectedEntry.value.end_time || !selectedDate.value) return '0.00'
  
  const hours = calculateAllHours(
    selectedDate.value,
    selectedEntry.value.start_time,
    selectedEntry.value.end_time,
    selectedEntry.value.break_duration || 0
  )
  
  return hours.ob1.toFixed(2)
})

const calculatedOB2 = computed(() => {
  if (!selectedEntry.value.start_time || !selectedEntry.value.end_time || !selectedDate.value) return '0.00'
  
  const hours = calculateAllHours(
    selectedDate.value,
    selectedEntry.value.start_time,
    selectedEntry.value.end_time,
    selectedEntry.value.break_duration || 0
  )
  
  return hours.ob2.toFixed(2)
})

const calculatedOB3 = computed(() => {
  if (!selectedEntry.value.start_time || !selectedEntry.value.end_time || !selectedDate.value) return '0.00'
  
  const hours = calculateAllHours(
    selectedDate.value,
    selectedEntry.value.start_time,
    selectedEntry.value.end_time,
    selectedEntry.value.break_duration || 0
  )
  
  return hours.ob3.toFixed(2)
})

const requiresLegalBreakComputed = computed(() =>
  requiresLegalBreak(selectedEntry.value.start_time, selectedEntry.value.end_time)
)

// Track if user manually modified break from required amount
const hasBreakDiscrepancy = ref(false)

// Watch for changes in start/end time to auto-set required break
watch([() => selectedEntry.value.start_time, () => selectedEntry.value.end_time], () => {
  const requiredBreak = calculateRequiredBreak(selectedEntry.value.start_time, selectedEntry.value.end_time)
  
  if (requiredBreak > 0) {
    // Only auto-set if break is currently 0 or this is a new shift
    if (selectedEntry.value.break_duration === 0 || selectedEntry.value.break_duration === null) {
      selectedEntry.value.break_duration = requiredBreak
      hasBreakDiscrepancy.value = false
    }
  } else {
    // For shifts under 5.5 hours, reset to 0 if it was set by auto-calculation
    if (!hasBreakDiscrepancy.value && selectedEntry.value.break_duration > 0) {
      selectedEntry.value.break_duration = 0
    }
  }
}, { immediate: false })

// Watch for manual changes to break duration
watch(() => selectedEntry.value.break_duration, (newValue, oldValue) => {
  const requiredBreak = calculateRequiredBreak(selectedEntry.value.start_time, selectedEntry.value.end_time)
  
  if (requiredBreak > 0) {
    // If break is restored to required amount, clear discrepancy
    if (newValue === requiredBreak) {
      hasBreakDiscrepancy.value = false
    } 
    // If break is changed from required amount to something else, flag as discrepancy
    else if (oldValue === requiredBreak && newValue !== requiredBreak) {
      hasBreakDiscrepancy.value = true
    }
  } else {
    // Reset discrepancy flag for shifts that don't require break
    hasBreakDiscrepancy.value = false
  }
})

watch(() => shiftStore.lastUpdate, async () => {
  // Re-fetch assigned shifts for the user
  if (!authStore.user || !authStore.user.id) return;
  const startDate = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), 1, 0, 0, 0)
  const endDate = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 0, 23, 59, 59)
  const shifts = await shiftApi.getWorkerSchedule(authStore.user.id, startDate, endDate)
  
  // Organize shifts by date, excluding rejected shifts
  assignedShifts.value = {};
  shifts.forEach(shift => {
    // Skip rejected shifts - they should not appear in worker's calendar
    const status = shift.assignment?.status || 'approved'
    if (status === 'rejected') {
      return // Don't add rejected shifts to the calendar
    }

    const shiftDate = shift.start_time.split('T')[0]; // Extract date (YYYY-MM-DD)
    if (!assignedShifts.value[shiftDate]) {
      assignedShifts.value[shiftDate] = [];
    }
    assignedShifts.value[shiftDate].push(shift);
  });
})

const workingDaysCountComputed = computed(() =>
  workingDaysCount(timeEntries.value, currentDate.value)
)

const averageHoursPerDayComputed = computed(() =>
  averageHoursPerDay(monthlyReportedHoursComputed.value, workingDaysCountComputed.value)
)

const monthlyShiftHoursComputed = computed(() =>
  monthlyHours(assignedShifts.value, currentDate.value)
)

const monthlyReportedHoursComputed = computed(() =>
  monthlyHours(timeEntries.value, currentDate.value)
)

const workerShiftMismatch = computed(() => {
  if (!selectedDate.value || !selectedEntry.value.start_time || !selectedEntry.value.end_time) return false;
  
  const assigned = assignedShifts.value[selectedDate.value];
  
  if (!assigned || assigned.length === 0) {
    return false;
  }
  
  // Get entry start and end times (NOT duration, to ignore break changes)
  const entryStart = selectedEntry.value.start_time;
  const entryEnd = selectedEntry.value.end_time;
  const entryStartMin = parseTimeToMinutes(entryStart);
  const entryEndMin = parseTimeToMinutes(entryEnd);
  
  // Find closest assigned shift by start/end time
  let closestShift = assigned.reduce((best, shift) => {
    // Extract time from datetime string (shift times come as full datetime from backend)
    const shiftStartTime = shift.start_time.includes('T') ? shift.start_time.split('T')[1].substring(0, 5) : shift.start_time;
    const shiftEndTime = shift.end_time.includes('T') ? shift.end_time.split('T')[1].substring(0, 5) : shift.end_time;
    
    const shiftStartMin = parseTimeToMinutes(shiftStartTime);
    const shiftEndMin = parseTimeToMinutes(shiftEndTime);
    
    const diff = Math.abs(shiftStartMin - entryStartMin) + Math.abs(shiftEndMin - entryEndMin);
    if (!best || diff < best.diff) {
      return { diff, startMin: shiftStartMin, endMin: shiftEndMin, startTime: shiftStartTime, endTime: shiftEndTime };
    }
    return best;
  }, null);
  
  // Check if start or end time differs from assigned shift (allow 5 minute tolerance)
  // This only checks TIME changes, not break duration changes
  const startMismatch = Math.abs(entryStartMin - closestShift.startMin) > 5;
  const endMismatch = Math.abs(entryEndMin - closestShift.endMin) > 5;
  
  return startMismatch || endMismatch;
});


onMounted(async () => {
  // Check if user is authenticated and user data is loaded
  if (!authStore.isAuthenticated || !authStore.user) {
    router.push('/')
    return
  }
  //Fetch user data first
  await fetchUserData();

  // Initialize shiftStore with current date
  shiftStore.setCurrentDate(currentDate.value)

  // Fetch assigned shifts for the month only if user is available
  if (authStore.user && authStore.user.id) {
    const startDate = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), 1);
    const endDate = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 0);
    const shifts = await shiftApi.getWorkerSchedule(authStore.user.id, startDate, endDate);
    
    // Organize shifts by date
    assignedShifts.value = {};
    shifts.forEach(shift => {
      const shiftDate = shift.start_time.split('T')[0]; // Extract date (YYYY-MM-DD)
      if (!assignedShifts.value[shiftDate]) {
        assignedShifts.value[shiftDate] = [];
      }
      assignedShifts.value[shiftDate].push(shift);
    });
  }
  
  // Force all time inputs to use 24-hour format
  setTimeout(() => {
    const timeInputs = document.querySelectorAll('input[type="time"]')
    timeInputs.forEach(input => {
      input.setAttribute('lang', 'sv-SE')
      input.style.setProperty('-webkit-locale', 'sv-SE', 'important')
    })
  }, 100)
  
  loadTimeEntries()
})

// Fetch user data
async function fetchUserData() {
  try {
    // Get user data from the Pinia store
    if (authStore.user) {
      user.value = authStore.user
    }
  } catch (e) {
    console.error('Error fetching user data:', e)
  }
}

// Watch for month changes to reload data
watch(currentDate, async (newDate) => {
  if (!authStore.user || !authStore.user.id) return;

  // Clear existing data first to ensure reactivity
  timeEntries.value = {}
  assignedShifts.value = {}

  const startDate = new Date(newDate.getFullYear(), newDate.getMonth(), 1, 0, 0, 0);
  const endDate = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0, 23, 59, 59);
  const shifts = await shiftApi.getWorkerSchedule(authStore.user.id, startDate, endDate);

  // Organize shifts by date, excluding rejected shifts
  shifts.forEach(shift => {
    // Skip rejected shifts - they should not appear in worker's calendar
    const status = shift.assignment?.status || 'approved'
    if (status === 'rejected') {
      return // Don't add rejected shifts to the calendar
    }

    const shiftDate = shift.start_time.split('T')[0]; // Extract date (YYYY-MM-DD)
    if (!assignedShifts.value[shiftDate]) {
      assignedShifts.value[shiftDate] = [];
    }
    assignedShifts.value[shiftDate].push(shift);
  });
  
  loadTimeEntries()
});

// Approve a pending shift
async function approveShift(shiftId) {
  loading.value = true;
  message.value = '';
  try {
    // Find the assignmentId for this shift
    let assignmentId = null;
    for (const date in assignedShifts.value) {
      const found = assignedShifts.value[date].find(s => s.id === shiftId);
      if (found && found.assignment) {
        assignmentId = found.assignment.id;
        break;
      }
    }
    if (!assignmentId) throw new Error(t('timeReporting.assignmentNotFound'));
    await shiftApi.updateAssignmentDetails(assignmentId, { status: 'approved' });
    message.value = t('timeReporting.shiftApproved');
    messageType.value = 'success';
    
    // Notify store to trigger reactivity in manager's calendar
    shiftStore.notifyShiftsChanged();
    
    await loadTimeEntries();
  } catch (error) {
    message.value = t('timeReporting.failedToApprove') + ': ' + error.message;
    messageType.value = 'error';
  } finally {
    loading.value = false;
  }
}

// Reject a pending shift
async function rejectShift(shiftId) {
  loading.value = true;
  message.value = '';
  try {
    // Find the assignmentId for this shift
    let assignmentId = null;
    for (const date in assignedShifts.value) {
      const found = assignedShifts.value[date].find(s => s.id === shiftId);
      if (found && found.assignment) {
        assignmentId = found.assignment.id;
        break;
      }
    }
    if (!assignmentId) throw new Error(t('timeReporting.assignmentNotFound'));
    await shiftApi.updateAssignmentDetails(assignmentId, { status: 'rejected' });
    message.value = t('timeReporting.shiftRejected');
    messageType.value = 'success';
    
    // Notify store to trigger reactivity in manager's calendar
    shiftStore.notifyShiftsChanged();
    
    await loadTimeEntries();
  } catch (error) {
    message.value = t('timeReporting.failedToReject') + ': ' + error.message;
    messageType.value = 'error';
  } finally {
    loading.value = false;
  }
}

// Open pending shift modal (for mobile)
function openPendingShiftModal(shift) {
  selectedPendingShift.value = {
    ...shift,
    date: selectedDate.value
  }
  showPendingShiftModal.value = true
}

function closePendingShiftModal() {
  showPendingShiftModal.value = false
  selectedPendingShift.value = null
}

async function approveShiftAndClose(shiftId) {
  await approveShift(shiftId)
  closePendingShiftModal()
}

async function rejectShiftAndClose(shiftId) {
  await rejectShift(shiftId)
  closePendingShiftModal()
}

function formatFullDate(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('sv-SE', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })
}

function previousMonth() {
  currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() - 1, 1)
  shiftStore.setCurrentDate(currentDate.value)
}

function nextMonth() {
  currentDate.value = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 1)
  shiftStore.setCurrentDate(currentDate.value)
}

// PDF Export function
function exportToPDF() {
  const doc = new jsPDF()
  
  // Get user info from fetched user data
  const userName = user.value 
    ? `${user.value.first_name} ${user.value.last_name}`.trim()
    : localStorage.getItem('user_name') || localStorage.getItem('username') || 'Employee Name'
  const monthYear = formatMonthYear(currentDate.value)
  
  // Add logo to header (if available)
  try {
    // Try to load logo from public folder
    const logoImg = new Image()
    logoImg.onload = function() {
      // Add logo to top-left corner
      doc.addImage(logoImg, 'PNG', 15, 8, 42, 20)
      
      // Title (moved right to accommodate logo)
      doc.setFontSize(16)
      doc.text(`Tidsraport`, 105, 20, { align: 'center' })
      
      doc.setFontSize(12)
      doc.text(`Anställd: ${user.value.first_name} ${user.value.last_name}`, 20, 35)
      doc.text(`Månad: ${monthYear}`, 150, 35)
      
      // Continue with table generation after logo is loaded
      generatePDFTable(doc, monthYear)
    }
    logoImg.onerror = function() {
      // If logo fails to load, continue without logo
      generatePDFWithoutLogo(doc, monthYear)
    }
    logoImg.src = '/logo.png' // Path to logo in public folder
  } catch (error) {
    // If any error occurs, continue without logo
    generatePDFWithoutLogo(doc, monthYear)
  }
}

function generatePDFWithoutLogo(doc, monthYear) {
  // Title
  doc.setFontSize(16)
  doc.text(`Tidsraport`, 105, 15, { align: 'center' })
  
  doc.setFontSize(12)
  doc.text(`Anställd: ${user.value.first_name} ${user.value.last_name}`, 20, 25)
  doc.text(`Månad: ${monthYear}`, 150, 25)
  
  generatePDFTable(doc, monthYear)
}

function generatePDFTable(doc, monthYear) {
  // Prepare table data
  const tableData = []
  
  // Get all days in current month
  const year = currentDate.value.getFullYear()
  const month = currentDate.value.getMonth()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  
  for (let day = 1; day <= daysInMonth; day++) {
    // Create date string directly to avoid timezone issues
    const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
    const date = new Date(year, month, day)
    const dayEntries = timeEntries.value[dateStr] || []
    
    if (dayEntries.length === 0) {
      // Empty day - add one row with empty cells
      tableData.push([
        `${day.toString().padStart(2, '0')}/${(month + 1).toString().padStart(2, '0')}`,
        date.toLocaleDateString('sv-SE', { weekday: 'short' }),
        '', '', '', '', ''
      ])
    } else {
      // Days with entries
      dayEntries.forEach((entry, index) => {
        // Use calculateAllHours for consistent Swedish collective agreement calculations
        const allHours = calculateAllHours(dateStr, entry.start_time, entry.end_time, entry.break_duration || 0)
        const totalHours = (allHours.regular + allHours.ob1 + allHours.ob2 + allHours.ob3).toFixed(2)
        const ob1Hours = allHours.ob1.toFixed(2)
        const ob2Hours = allHours.ob2.toFixed(2)
        const ob3Hours = allHours.ob3.toFixed(2)
        
        const rowData = [
          index === 0 ? `${day.toString().padStart(2, '0')}/${(month + 1).toString().padStart(2, '0')}` : '',
          index === 0 ? date.toLocaleDateString('sv-SE', { weekday: 'short' }) : '',
          parseFloat(totalHours) > 0 ? totalHours : '',
          parseFloat(ob1Hours) > 0 ? ob1Hours : '',
          parseFloat(ob2Hours) > 0 ? ob2Hours : '',
          parseFloat(ob3Hours) > 0 ? ob3Hours : '',
          entry.description || ''
        ]
        tableData.push(rowData)
      })
    }
  }
  
  // Add totals row
  const totalHours = tableData.reduce((sum, row) => sum + (parseFloat(row[2]) || 0), 0)
  const totalOB1 = tableData.reduce((sum, row) => sum + (parseFloat(row[3]) || 0), 0)
  const totalOB2 = tableData.reduce((sum, row) => sum + (parseFloat(row[4]) || 0), 0)
  const totalOB3 = tableData.reduce((sum, row) => sum + (parseFloat(row[5]) || 0), 0)
  
  tableData.push([
    'TOTALT',
    '',
    totalHours > 0 ? totalHours.toFixed(2) : '',
    totalOB1 > 0 ? totalOB1.toFixed(2) : '',
    totalOB2 > 0 ? totalOB2.toFixed(2) : '',
    totalOB3 > 0 ? totalOB3.toFixed(2) : '',
    ''
  ])
  
  // Create table with larger text and better page utilization
  autoTable(doc, {
    head: [['Datum', 'Veckodag', 'Arbetade tim', 'OB1', 'OB2', 'OB3', 'Kommentar']],
    body: tableData,
    startY: 40, // Adjusted to accommodate logo and header
    styles: { 
      fontSize: 8,
      cellPadding: 2,
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
      minCellHeight: 6
    },
    headStyles: { 
      fillColor: [41, 128, 185],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
      minCellHeight: 8
    },
    columnStyles: {
      0: { cellWidth: 25, halign: 'center' },
      1: { cellWidth: 25, halign: 'center' },
      2: { cellWidth: 25, halign: 'right' },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 22, halign: 'right' },
      5: { cellWidth: 22, halign: 'right' },
      6: { cellWidth: 45, halign: 'left' }
    },
    margin: { top: 10, left: 15, right: 15, bottom: 10 },
    tableWidth: 'wrap',
    didParseCell: function(data) {
      // Style the total row
      if (data.row.index === tableData.length - 1) {
        data.cell.styles.fillColor = [231, 76, 60]
        data.cell.styles.textColor = [255, 255, 255]
        data.cell.styles.fontStyle = 'bold'
        data.cell.styles.fontSize = 9
      }
    }
  })
  
  // Add footer
  const pageHeight = doc.internal.pageSize.height
  doc.setFontSize(7)
  doc.setTextColor(100, 100, 100) // Gray color
  doc.text('* OB1: vardagar efter kl.18:15, OB2: vardagar efter kl. 20:00, OB3: lördagar efter kl. 12, söndagar + röda dagar', 15, pageHeight - 15)
  
  // Save the PDF
  const userLastName = user.value?.last_name || 'Employee'
  const fileName = `TimeReport_${userLastName}_${monthYear.replace(' ', '_')}.pdf`
  doc.save(fileName)
}

function formatMonthYear(date) {
  const localeCode = locale.value === 'sv' ? 'sv-SE' : 'en-US'
  const monthYear = date.toLocaleDateString(localeCode, { month: 'long', year: 'numeric' })
  // Convert to camelCase: capitalize first letter, lowercase the rest
  return monthYear.charAt(0).toUpperCase() + monthYear.slice(1).toLowerCase()
}

function getDayClasses(day) {
  const hasRejectedShifts = day.timeEntries && day.timeEntries.some(e => e.status === 'rejected')
  return {
    'calendar-day': true,
    'current-month': day.isCurrentMonth,
    'other-month': !day.isCurrentMonth,
    'today': day.isToday,
    'has-entry': day.timeEntries && day.timeEntries.length > 0,
    'has-rejected': hasRejectedShifts,
    'sick-leave-day': day.isSickLeave,
    'clickable': day.isCurrentMonth && !day.isSickLeave
  }
}

function hasPendingShift(day) {
  return day.assignedShifts && day.assignedShifts.some(shift => shift.status === 'pending')
}

function selectDate(day) {
  if (!day.isCurrentMonth || !day.date) return
  
  // Prevent opening modal for sick leave days
  if (day.isSickLeave) {
    message.value = t('timeReporting.cannotEditSickLeaveDay') || 'Cannot add time entries on sick leave days'
    messageType.value = 'error'
    setTimeout(() => {
      message.value = ''
    }, 3000)
    return
  }
  
  selectedDate.value = day.date
  selectedDateEntries.value = timeEntries.value[day.date] || []
  
  // Check if there are assigned shifts for this date
  const assignedShiftsForDate = assignedShifts.value[day.date] || []
  
  let defaultStartTime = '08:00'
  let defaultEndTime = '16:00'
  
  // If there's an assigned shift, use its times
  if (assignedShiftsForDate.length > 0) {
    // Use the first assigned shift (or the first not yet reported)
    const shift = assignedShiftsForDate[0]
    
    // Extract time from datetime format (if it's in ISO format)
    if (shift.start_time.includes('T')) {
      defaultStartTime = shift.start_time.split('T')[1].substring(0, 5)
    } else {
      defaultStartTime = shift.start_time
    }
    
    if (shift.end_time.includes('T')) {
      defaultEndTime = shift.end_time.split('T')[1].substring(0, 5)
    } else {
      defaultEndTime = shift.end_time
    }
  }
  
  // Calculate if the times require a break
  const defaultRequiresBreak = requiresLegalBreak(defaultStartTime, defaultEndTime)
  
  // Reset form for new shift with auto-filled times
  selectedEntry.value = {
    start_time: defaultStartTime,
    end_time: defaultEndTime,
    break_duration: defaultRequiresBreak ? 30 : 0,
    description: ''
  }
  editingShiftIndex.value = null
  hasBreakDiscrepancy.value = false
  
  showModal.value = true
}

function editShift(index) {
  const shift = selectedDateEntries.value[index]
  selectedEntry.value = { ...shift }
  editingShiftIndex.value = index
  hasBreakDiscrepancy.value = false // Reset flag when editing
}

function cancelShiftEdit() {
  // Check if there are assigned shifts for this date
  const assignedShiftsForDate = assignedShifts.value[selectedDate.value] || []
  
  let defaultStartTime = '08:00'
  let defaultEndTime = '16:00'
  
  // If there's an assigned shift, use its times
  if (assignedShiftsForDate.length > 0) {
    const shift = assignedShiftsForDate[0]
    
    // Extract time from datetime format (if it's in ISO format)
    if (shift.start_time.includes('T')) {
      defaultStartTime = shift.start_time.split('T')[1].substring(0, 5)
    } else {
      defaultStartTime = shift.start_time
    }
    
    if (shift.end_time.includes('T')) {
      defaultEndTime = shift.end_time.split('T')[1].substring(0, 5)
    } else {
      defaultEndTime = shift.end_time
    }
  }
  
  // Calculate if the times require a break
  const defaultRequiresBreak = requiresLegalBreak(defaultStartTime, defaultEndTime)
  
  selectedEntry.value = {
    start_time: defaultStartTime,
    end_time: defaultEndTime,
    break_duration: defaultRequiresBreak ? 30 : 0,
    description: ''
  }
  editingShiftIndex.value = null
  hasBreakDiscrepancy.value = false
  
  // Close the modal
  closeModal()
}

async function deleteShift(shiftId, index) {
  showConfirm('Are you sure you want to delete this shift?', async () => {
    loading.value = true
    try {
      await timeEntryApi.deleteEntry(shiftId)
      // Remove from local state
      selectedDateEntries.value.splice(index, 1)
      if (!timeEntries.value[selectedDate.value]) {
        timeEntries.value[selectedDate.value] = []
      }
      timeEntries.value[selectedDate.value] = selectedDateEntries.value
      // Recalculate OB hours for this date after deletion (non-blocking)
      saveOBHoursForDate(selectedDate.value).catch(obError => {
        console.warn('Could not update OB hours after deletion:', obError)
      })
      // Notify all listeners (other tabs/components) that shifts changed
      shiftStore.notifyShiftsChanged()
      message.value = 'Shift deleted successfully!'
      messageType.value = 'success'
    } catch (error) {
      console.error('Error deleting shift:', error)
      message.value = 'Failed to delete shift: ' + error.message
      messageType.value = 'error'
    } finally {
      loading.value = false
    }
  })
}

function closeModal() {
  showModal.value = false
  selectedDate.value = null
  selectedDateEntries.value = []
  editingShiftIndex.value = null
  hasBreakDiscrepancy.value = false
  selectedEntry.value = {
    start_time: '',
    end_time: '',
    break_duration: 0,
    description: ''
  }
}

// Confirmation modal functions
function showConfirm(message, callback) {
  confirmMessage.value = message
  confirmCallback.value = callback
  showConfirmModal.value = true
}

function confirmAction() {
  showConfirmModal.value = false
  if (confirmCallback.value) {
    confirmCallback.value()
  }
  confirmCallback.value = null
}

function cancelConfirm() {
  showConfirmModal.value = false
  confirmCallback.value = null
}

// Sick leave logic
const hasAssignedShiftForSelectedDate = computed(() => {
  if (!selectedDate.value) return false
  const shifts = assignedShifts.value[selectedDate.value]
  return shifts && shifts.length > 0
})

const hasSickLeaveForSelectedDate = computed(() => {
  if (!selectedDate.value) return false
  return sickLeaveDays.value[selectedDate.value] || false
})

const isSelectedDateInPast = computed(() => {
  if (!selectedDate.value) return false
  const selected = new Date(selectedDate.value)
  const today = new Date()
  // Set both to midnight for accurate comparison
  selected.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)
  return selected < today
})

async function reportSickLeave() {
  if (!hasAssignedShiftForSelectedDate.value || !selectedDate.value || hasSickLeaveForSelectedDate.value || isSelectedDateInPast.value) return
  
  loading.value = true
  message.value = ''
  
  try {
    // Create sick leave record for the selected date
    const sickLeaveResponse = await sickLeaveApi.createSickLeave({
      worker: authStore.user.id,
      start_date: selectedDate.value,
      end_date: selectedDate.value,
      filled_by_manager: false
    })
    
    message.value = t('timeReporting.sickLeaveReported') || 'Sick leave reported successfully'
    messageType.value = 'success'
    showModal.value = false
    
    // Mark this date as sick leave locally
    sickLeaveDays.value[selectedDate.value] = true
    
    // Dispatch global event to notify other components (e.g., UserInfo)
    window.dispatchEvent(new CustomEvent('sick-leave-reported', { 
      detail: { 
        workerId: authStore.user.id, 
        date: selectedDate.value 
      } 
    }))
    
    // Reload time entries to reflect any changes
    await loadTimeEntries()
  } catch (error) {
    console.error('Error reporting sick leave:', error)
    message.value = (t('timeReporting.failedToReportSickLeave') || 'Failed to report sick leave') + ': ' + error.message
    messageType.value = 'error'
  } finally {
    loading.value = false
  }
}

async function saveShift() {
  loading.value = true
  message.value = ''
  
  try {
    // Determine status based on discrepancies
    // submitted_ok: No issues, ready for auto-approval
    // submitted_pending: Has discrepancies, needs manager review
    const hasShiftMismatch = workerShiftMismatch.value
    const hasBreakIssue = hasBreakDiscrepancy.value
    const autoStatus = (hasShiftMismatch || hasBreakIssue) ? 'submitted_pending' : 'submitted_ok'
    
    // Only include fields that can be updated
    const entryData = {
      start_time: selectedEntry.value.start_time,
      end_time: selectedEntry.value.end_time,
      break_duration: parseInt(selectedEntry.value.break_duration) || 0,
      description: selectedEntry.value.description,
      status: autoStatus,
      break_discrepancy: hasBreakIssue,
      shift_mismatch: hasShiftMismatch
    }
    
    let savedEntry
    
    if (editingShiftIndex.value !== null) {
      // Update existing shift - include date as it's required by backend
      const existingShift = selectedDateEntries.value[editingShiftIndex.value]
      const updateData = {
        ...entryData,
        date: selectedDate.value // Include date for updates
      }
      savedEntry = await timeEntryApi.updateEntry(existingShift.id, updateData)
      
      // Update in local arrays
      const updatedShift = {
        id: savedEntry.id,
        start_time: savedEntry.start_time,
        end_time: savedEntry.end_time,
        break_duration: savedEntry.break_duration,
        description: savedEntry.description,
        status: savedEntry.status,
        totalHours: calculateTotalHours(savedEntry.start_time, savedEntry.end_time, savedEntry.break_duration)
      }
      
      selectedDateEntries.value[editingShiftIndex.value] = updatedShift
      
    } else {
      // Create new shift - include date and let backend set user
      const createData = { 
        date: selectedDate.value,
        start_time: selectedEntry.value.start_time,
        end_time: selectedEntry.value.end_time,
        break_duration: parseInt(selectedEntry.value.break_duration) || 0,
        description: selectedEntry.value.description,
        status: autoStatus,
        break_discrepancy: hasBreakIssue,
        shift_mismatch: hasShiftMismatch
      }
      savedEntry = await timeEntryApi.createEntry(createData)
      const newShift = {
        id: savedEntry.id,
        start_time: savedEntry.start_time,
        end_time: savedEntry.end_time,
        break_duration: savedEntry.break_duration,
        description: savedEntry.description,
        status: savedEntry.status,
        totalHours: calculateTotalHours(savedEntry.start_time, savedEntry.end_time, savedEntry.break_duration)
      }
      selectedDateEntries.value.push(newShift)
    }
    
    // Update main timeEntries object
    timeEntries.value[selectedDate.value] = [...selectedDateEntries.value]
    
    // Calculate and save OB hours for this date (non-blocking)
    saveOBHoursForDate(selectedDate.value).catch(obError => {
      console.warn('Could not save OB hours (backend may need upsert logic):', obError)
      // Don't show error to user as shift was saved successfully
    })
    
    message.value = editingShiftIndex.value !== null ? 'Shift updated successfully!' : 'Shift added successfully!'
    messageType.value = 'success'
    
    // Dispatch event to notify other components (e.g., UserInfo) to reload hours
    window.dispatchEvent(new CustomEvent('time-entry-saved', { 
      detail: { workerId: authStore.user.id, date: selectedDate.value } 
    }))
    
    // Reset form
    cancelShiftEdit()
    
  } catch (error) {
    console.error('Error saving shift:', error)
    
    // Handle specific validation errors
    if (error.message.includes('future dates')) {
      message.value = 'Cannot create entries for future dates. Please contact your manager to enable future planning.'
      messageType.value = 'error'
    } else if (error.message.includes('non_field_errors')) {
      message.value = 'Validation error: ' + error.message.replace('Validation error: non_field_errors: ', '')
      messageType.value = 'error'
    } else {
      message.value = 'Failed to save shift: ' + error.message
      messageType.value = 'error'
    }
  } finally {
    loading.value = false
  }
}

// Calculate and save OB hours for a specific date
async function saveOBHoursForDate(date) {
  const dateEntries = timeEntries.value[date] || []

  
  // Calculate total OB hours for all shifts on this date using proper break deduction
  let totalOB1 = 0
  let totalOB2 = 0 
  let totalOB3 = 0
  
  dateEntries.forEach(entry => {
    
    // Use the new calculateAllHours function that properly deducts breaks
    const hours = calculateAllHours(date, entry.start_time, entry.end_time, entry.break_duration || 0)
    
    totalOB1 += hours.ob1
    totalOB2 += hours.ob2
    totalOB3 += hours.ob3
  })
  
  // Always save OB hours (backend handles create/update, even if zeros)
  await obHoursApi.saveOBHours(
    user.value?.id,
    date,
    parseFloat(totalOB1.toFixed(2)),
    parseFloat(totalOB2.toFixed(2)),
    parseFloat(totalOB3.toFixed(2))
  )
}



// async function deleteEntry() {

async function loadTimeEntries() {
  loading.value = true
  try {
    const year = currentDate.value.getFullYear()
    const month = currentDate.value.getMonth() + 1

    // Clear cache to ensure fresh data for the new month
    timeEntryApi.clearCache()
    
    // Load time entries
    const entries = await timeEntryApi.getMonthlyEntries(year, month)

    // Group entries by date and convert to arrays
    timeEntries.value = {}
    entries.forEach(entry => {
      // entry.user is now an object, not an ID
      if (entry.user && entry.user.id === authStore.user.id) { // Ensure filtering by logged-in user
        const dateKey = entry.date
        if (!timeEntries.value[dateKey]) {
          timeEntries.value[dateKey] = []
        }
        // Extract time portion if it's a full datetime string
        let startTime = entry.start_time
        let endTime = entry.end_time
        
        if (startTime && startTime.includes('T')) {
          startTime = startTime.split('T')[1].substring(0, 5) // Get HH:MM
        }
        if (endTime && endTime.includes('T')) {
          endTime = endTime.split('T')[1].substring(0, 5) // Get HH:MM
        }
        
        timeEntries.value[dateKey].push({
          id: entry.id,
          start_time: entry.start_time,
          end_time: entry.end_time,
          break_duration: entry.break_duration,
          description: entry.description,
          status: entry.status,
          totalHours: calculateTotalHours(startTime, endTime, entry.break_duration)
        })
      }
    })

    // Load assigned shifts for the current user
    if (authStore.user && authStore.user.id) {
      // Create dates at midnight local time to avoid timezone issues
      const startDate = new Date(year, month - 1, 1, 0, 0, 0) // First day of month
      const endDate = new Date(year, month, 0, 23, 59, 59) // Last day of month, include full day

      const shifts = await shiftApi.getWorkerSchedule(authStore.user.id, startDate, endDate)

      // Group shifts by date
      assignedShifts.value = {}
      shifts.forEach(shift => {
        const shiftDate = new Date(shift.start_time)
        const dateKey = `${shiftDate.getFullYear()}-${String(shiftDate.getMonth() + 1).padStart(2, '0')}-${String(shiftDate.getDate()).padStart(2, '0')}`

        // Skip rejected shifts - they should not appear in worker's calendar
        const status = shift.assignment?.status || 'approved'
        if (status === 'rejected') {
          return // Don't add rejected shifts to the calendar
        }

        // The shifts are already filtered by user on the backend, no need to filter again
        // shift.assignment contains the assignment details
        if (!assignedShifts.value[dateKey]) {
          assignedShifts.value[dateKey] = []
        }

        assignedShifts.value[dateKey].push({
          id: shift.id,
          start_time: shift.start_time,
          end_time: shift.end_time,
          assignment: shift.assignment, // Contains absence info
          status: status, // Get status from assignment
          duration: calculateShiftDuration(shift.start_time, shift.end_time)
        })
      })
    }
    
    // Load sick leaves for the current user
    if (authStore.user && authStore.user.id) {
      const startDateStr = `${year}-${String(month).padStart(2, '0')}-01`
      const endDateStr = new Date(year, month, 0).toISOString().split('T')[0]
      
      const sickLeaves = await sickLeaveApi.getSickLeaves({
        worker_id: authStore.user.id,
        start_date: startDateStr,
        end_date: endDateStr
      })
      
      // Mark sick leave days
      sickLeaveDays.value = {}
      sickLeaves.forEach(sickLeave => {
        const startDate = new Date(sickLeave.start_date)
        const endDate = new Date(sickLeave.end_date)
        
        // Mark all days in the range
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
          sickLeaveDays.value[dateKey] = true
        }
      })
    }

  } catch (error) {
    console.error('Error loading time entries:', error)
    message.value = 'Failed to load time entries: ' + error.message
    messageType.value = 'error'
  } finally {
    loading.value = false
  }
}

// ONE-TIME MIGRATION: Recalculate all OB hours for existing time entries
// Call this function once from browser console: window.recalculateAllOBHours()
async function recalculateAllOBHours() {
  
  let processedDates = 0
  let errorDates = 0
  
  for (const date in timeEntries.value) {
    try {
      await saveOBHoursForDate(date)
      processedDates++
    } catch (error) {
      errorDates++
    }
  }
  
  return { processedDates, errorDates }
}

// DEBUG: Test function for month change reactivity
function testMonthChangeReactivity() {
  return {
    currentMonth: `${currentDate.value.getFullYear()}-${String(currentDate.value.getMonth() + 1).padStart(2, '0')}`,
    timeEntriesDays: Object.keys(timeEntries.value).length,
    assignedShiftsDays: Object.keys(assignedShifts.value).length,
    monthlyShiftHours: monthlyShiftHoursComputed.value,
    monthlyReportedHours: monthlyReportedHoursComputed.value
  }
}

// Expose the migration function to window for one-time use
if (typeof window !== 'undefined') {
  window.recalculateAllOBHours = recalculateAllOBHours
  window.testTimeReportingReactivity = testMonthChangeReactivity
  window.timeReportingCurrentDate = currentDate
  window.timeReportingData = {
    timeEntries,
    assignedShifts,
    monthlyShiftHoursComputed,
    monthlyReportedHoursComputed
  }
}
</script>

<style scoped>
.time-reporting-container {
  position: relative;
  margin: 1rem auto;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.export-btn {
  padding: 0.75rem 1.5rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.export-btn.floating {
  position: fixed;
  top: 8rem; /* Just below AppBar (70px height + 10px gap) */
  right: 3rem;
  z-index: 999;
}

.export-btn:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(40, 167, 69, 0.4);
}

.export-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  box-shadow: none;
  opacity: 0.6;
}

.month-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.nav-btn {
  padding: 0.5rem 1rem;
  background: #2196f3;
  color: white;
  border: none;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
}

.nav-btn:hover {
  background: #1976d2;
}

.current-month {
  margin: 0;
  font-size: 1.5rem;
  color: #333;
}

.calendar-container {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.calendar-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  table-layout: fixed; /* Ensure equal column widths */
}

.calendar-table th {
  background: #2196f3;
  color: white;
  padding: 1rem;
  text-align: center;
  font-weight: bold;
  width: 14.28%; /* 100% / 7 days = 14.28% each */
}

.calendar-table td {
  border: 1px solid #e0e0e0;
  height: 80px;
  vertical-align: top;
  position: relative;
  width: 14.28%; /* 100% / 7 days = 14.28% each */
}

.calendar-day {
  height: 100%;
  padding: 0;
  position: relative;
}

.calendar-day.clickable {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.calendar-day.clickable:hover {
  background: #f5f5f5;
}

.calendar-day.current-month {
  background: white;
}

.calendar-day.other-month {
  background: #f9f9f9;
}

.calendar-day.today {
  background: #e3f2fd !important;
}

.calendar-day.has-entry {
  background: #f0f8ff;
}

.calendar-day.has-entry:hover {
  background: #e6f3ff;
}

.calendar-day.has-rejected {
  background: repeating-linear-gradient(
    135deg,
    #ffebee,
    #ffebee 10px,
    #ffcdd2 10px,
    #ffcdd2 20px
  ) !important;
}

.calendar-day.has-rejected:hover {
  background: repeating-linear-gradient(
    135deg,
    #ffcdd2,
    #ffcdd2 10px,
    #ef9a9a 10px,
    #ef9a9a 20px
  ) !important;
}

.calendar-day.sick-leave-day {
  background: repeating-linear-gradient(
    -45deg,
    #ffcccb,
    #ffcccb 10px,
    #ff6b6b 10px,
    #ff6b6b 20px
  ) !important;
  cursor: not-allowed !important;
}

.calendar-day.sick-leave-day:hover {
  background: repeating-linear-gradient(
    -45deg,
    #ff9999,
    #ff9999 10px,
    #ff4444 10px,
    #ff4444 20px
  ) !important;
}

.calendar-day.has-pending-shift {
  background: repeating-linear-gradient(
    45deg,
    #fff3e0,
    #fff3e0 10px,
    #ffcc80 10px,
    #ffcc80 20px
  ) !important;
}

.calendar-day.has-pending-shift:hover {
  background: repeating-linear-gradient(
    45deg,
    #ffe0b2,
    #ffe0b2 10px,
    #ffb74d 10px,
    #ffb74d 20px
  ) !important;
}

.day-content {
  position: relative;
  padding: 0.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.day-number {
  font-weight: bold;
  color: #333;
  font-size: 0.9rem;
}

.day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.shift-times-row {
  margin-bottom: 0.25rem;
}

.shift-times {
  font-size: 0.65rem;
  color: #4caf50;
  font-weight: 500;
  display: block;
}

.status-dot {
  font-size: 0.8rem;
}

.status-dot.submitted {
  color: #2196f3;
}

.status-dot.approved {
  color: #4caf50;
}

.status-dot.draft {
  color: #ff9800;
}

.reported-time {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.65rem;
}

.reported-label {
  color: #666;
}

.reported-hours {
  font-weight: bold;
  color: #2196f3;
}

.time-summary {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
}

.shift-entry {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.shift-number {
  background: #666;
  color: white;
  padding: 0.1rem 0.3rem;
  border-radius: 8px;
  font-size: 0.6rem;
  font-weight: bold;
}

.total-hours {
  font-weight: bold;
  color: #1976d2;
  font-size: 0.7rem;
}

.hours {
  background: #2196f3;
  color: white;
  padding: 0.2rem 0.4rem;
  border-radius: 10px;
  font-weight: bold;
}

.status {
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 0.8rem;
  line-height: 1;
}

.status.complete {
  color: #4caf50;
}

.status.partial {
  color: #ff9800;
}

.status.approved {
  color: #4caf50; /* Green for approved */
}

.status.submitted {
  color: #2196f3; /* Blue for submitted */
}

.status.draft {
  color: #9e9e9e; /* Gray for draft */
}

.status.rejected {
  color: #f44336; /* Red for rejected */
}

.rejected {
  color: #f44336; /* Red for rejected */
}

/* Modal Styles */
.modal {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 700px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

/* Confirmation Modal */
.confirm-modal {
  max-width: 400px;
  text-align: center;
}

.confirm-modal .modal-body {
  margin: 1.5rem 0;
}

.confirm-modal .modal-body p {
  font-size: 1.1rem;
  color: #333;
  margin: 0;
}

.confirm-modal .modal-footer {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
}

.confirm-modal .cancel-btn,
.confirm-modal .delete-btn {
  min-width: 100px;
}

/* Existing Shifts Section */
.existing-shifts {
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.shift-item {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.shift-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.shift-label {
  font-weight: bold;
  color: #333;
}

.edit-btn, .delete-shift-btn {
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
}

.edit-btn {
  background: #2196f3;
  color: white;
  margin-right: 0.5rem;
}

.edit-btn:hover {
  background: #1976d2;
}

.delete-shift-btn {
  background: #f44336;
  color: white;
}

.delete-shift-btn:hover {
  background: #d32f2f;
}

.shift-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  color: #666;
}

.shift-hours {
  font-weight: bold;
  color: #2196f3;
}

/* Shift Form Section */
.shift-form {
  border-top: 1px solid #e0e0e0;
  padding-top: 1.5rem;
}

.shift-form h4 {
  margin: 0 0 1rem 0;
  color: #333;
}

/* Day Summary */
.day-summary {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #e3f2fd;
  border-radius: 8px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-item label {
  font-weight: bold;
  color: #555;
}

.summary-item span {
  font-weight: bold;
  color: #1976d2;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  color: #bbb;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
}
.modal-title {
  margin: 0;
  font-size: 1.5rem;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
}

.close-btn:hover {
  color: #333;
}

.modal-form .form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #555;
}

.form-group input,
.form-group textarea {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

/* Force 24-hour format for time inputs */
.form-group input[type="time"] {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: textfield;
  -webkit-locale: "sv-SE";
}

.form-group input[type="time"]::-webkit-datetime-edit-ampm-field {
  display: none !important;
  width: 0 !important;
  visibility: hidden !important;
}

.form-group input[type="time"]::-webkit-inner-spin-button {
  display: none;
}

/* Additional 24-hour format enforcement */
.form-group input[type="time"]::-webkit-datetime-edit-hour12-field {
  display: none !important;
  width: 0 !important;
  visibility: hidden !important;
}

.form-group input[type="time"]::-webkit-datetime-edit-meridiem-field {
  display: none !important;
  width: 0 !important;
  visibility: hidden !important;
}

/* Force all AM/PM related pseudo-elements to be hidden */
input[type="time"]::-webkit-datetime-edit-ampm-field,
input[type="time"]::-webkit-datetime-edit-meridiem-field,
input[type="time"]::-webkit-datetime-edit-hour12-field {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
  opacity: 0 !important;
  visibility: hidden !important;
  position: absolute !important;
  left: -9999px !important;
}

/* Enhanced time input styling */
.time-input {
  font-family: 'Segoe UI', monospace;
  font-size: 1.1rem !important;
  padding: 0.8rem !important;
  border: 2px solid #ddd !important;
  border-radius: 8px !important;
  background: #fff;
  transition: border-color 0.2s ease;
  text-align: center;
}

.time-input:focus {
  border-color: #667eea !important;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.time-input:hover {
  border-color: #bbb;
}

/* Time Calculations Section */
.time-calculations {
  margin: 1.5rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.time-calculations h4 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.1rem;
}

.calc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
}

.calc-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: white;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.calc-item.total {
  border-color: #2196f3;
  background: #e3f2fd;
  font-weight: bold;
}

.calc-item label {
  font-weight: 500;
  color: #555;
  margin: 0;
}

.calc-item.total label {
  color: #1976d2;
}

.calc-value {
  font-weight: bold;
  color: #2196f3;
  font-family: 'Courier New', monospace;
}

.calc-value.total {
  color: #1976d2;
  font-size: 1.1rem;
}

/* Break Warning Styles */
.required-indicator {
  color: #ff9800;
  font-weight: bold;
  font-size: 0.85rem;
}

.break-warning {
  border-color: #ff9800 !important;
  background-color: #fff3e0 !important;
}

.warning-message {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: #fff3e0;
  border: 1px solid #ff9800;
  border-radius: 4px;
  color: #ef6c00;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.discrepancy-message {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
  color: #c62828;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.calculated-hours {
  padding: 0.75rem;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-weight: bold;
  color: #2196f3;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

.modal-actions button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

.save-btn {
  background: #2196f3;
  color: white;
}

.save-btn:hover:not(:disabled) {
  background: #1976d2;
}

.cancel-btn {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
}

.cancel-btn:hover {
  background: #e9e9e9;
}

.delete-btn {
  background: #f44336;
  color: white;
}

.delete-btn:hover {
  background: #d32f2f;
}

.save-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* Monthly Summary */
.monthly-summary {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.subtitle {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.2rem;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
}

.stat-label {
  font-weight: bold;
  color: #555;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: bold;
  color: #2196f3;
}

/* Totals Section */
.totals-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.totals-section h3 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.2rem;
}

.totals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.totals-item {
  align-items: center;
   padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #ddd;
}

.totals-item label {
  font-weight: bold;
  color: #555;
}

.totals-item span {
  font-weight: bold;
  color: #1976d2;
}

/* Messages */
.message {
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
}

.message.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* Shift times in calendar */
.shift-times-row {
  margin-bottom: 0.15rem;
  line-height: 1.2;
}

.shift-times {
  font-size: 0.7rem;
  color: #2196f3;
  font-weight: 600;
}

.shift-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.shift-list-item {
  line-height: 1.3;
  margin-bottom: 2px;
}

/* Pending shift approval buttons */
.pending-shift {
  display: inline-flex;
  gap: 4px;
  margin-left: 6px;
}

.approve-btn, .reject-btn {
  padding: 2px 8px;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.approve-btn {
  background: #4caf50;
  color: white;
}

.approve-btn:hover {
  background: #45a049;
  transform: scale(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.reject-btn {
  background: #f44336;
  color: white;
}

.reject-btn:hover {
  background: #da190b;
  transform: scale(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

/* Clickable pending shift for mobile */
.clickable-pending {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
}

/* Desktop-only buttons (hidden on mobile) */
.desktop-only {
  display: inline-flex;
}

/* Pending Shift Approval Modal */
.pending-shift-modal {
  max-width: 400px;
  width: 90%;
}

.shift-detail {
  text-align: center;
  margin: 1.5rem 0;
}

.shift-date {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
}

.shift-time {
  font-size: 1.5rem;
  font-weight: bold;
  color: #2196f3;
  margin: 0.5rem 0;
}

.shift-duration {
  font-size: 0.9rem;
  color: #e0911b;
}

.approval-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.approve-btn-large, .reject-btn-large {
  flex: 1;
  padding: 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.approve-btn-large {
  background: #4caf50;
  color: white;
}

.approve-btn-large:hover {
  background: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.reject-btn-large {
  background: #f44336;
  color: white;
}

.reject-btn-large:hover {
  background: #da190b;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.sick-leave-row {
  margin: 1rem 0;
  text-align: center;
}
.sick-leave-btn {
  padding: 0.75rem 1.5rem;
  background: #e57373;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(229, 115, 115, 0.3);
}
.sick-leave-btn:disabled {
  background: #bdbdbd;
  cursor: not-allowed;
  box-shadow: none;
  opacity: 0.6;
}
.sick-leave-btn:hover:not(:disabled) {
  background: #d32f2f;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(229, 115, 115, 0.4);
}

/* Responsive Design */
@media (max-width: 768px) {
  .time-reporting-container {
    padding: 1rem;
  }
  
  .calendar-table td {
    height: 60px;
  }
  
  .day-content {
    padding: 0.25rem;
  }

  /* Improve readability on mobile */
  .calendar-day.has-entry {
    background: #fff;
    border: 2px solid #2196f3 !important;
  }

  .calendar-day.has-entry:hover {
    background: #f5f5f5;
  }

  .day-content {
    padding: 0.2rem;
    font-size: 0.65rem;
  }

  .day-number {
    font-weight: 700;
    color: #000;
    font-size: 0.85rem;
  }

  .day-header {
    margin-bottom: 0.1rem;
  }

  .shift-times-row {
    margin-bottom: 0.15rem;
    line-height: 1.2;
  }

  .shift-times {
    font-size: 0.6rem;
    color: #2196f3;
    font-weight: 600;
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .shift-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .shift-list-item {
    line-height: 1.1;
  }

  /* Hide desktop buttons on mobile, show modal trigger instead */
  .desktop-only {
    display: none !important;
  }

  .clickable-pending {
    cursor: pointer;
    font-weight: bold;
    padding: 2px 4px;
    background: #fff3e0;
    border-radius: 3px;
  }

  .reported-time {
    font-size: 0.6rem;
    gap: 0.2rem;
  }

  .reported-label {
    color: #333;
    font-weight: 600;
    white-space: nowrap;
  }

  .reported-hours {
    font-weight: 700;
    color: #2196f3;
    font-size: 0.6rem;
    white-space: nowrap;
  }

  .status-dot {
    font-size: 0.85rem;
  }

  .time-summary {
    font-size: 0.6rem;
    gap: 0.1rem;
  }

  .shift-entry {
    gap: 0.15rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .modal {
    padding: 1rem;
    width: 95%;
  }
  
  .modal-form .form-row {
    grid-template-columns: 1fr;
  }
  
  .summary-stats {
    grid-template-columns: 1fr;
  }

  /* Floating export button on mobile */
  .export-btn.floating {
    top: 70px; /* Closer to AppBar on mobile */
    right: 1rem;
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }
}
</style>